/** Human NPC moods, greetings, and pet offers (feeds Begging). */

export type NpcTemperament = "warm" | "even" | "cool";
export type NpcMood = "friendly" | "busy" | "cold";

const FRIEND_CAP = 120;

export function hashNpcId(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Stable personality - some humans are naturally warmer than others. */
export function npcTemperament(nodeId: string): NpcTemperament {
  const r = hashNpcId(nodeId) % 100;
  if (r < 42) {
    return "warm";
  }
  if (r < 78) {
    return "even";
  }
  return "cool";
}

/**
 * Moment-to-moment mood. Friends always get friendly.
 * Others drift on a ~2 min window so the same person isn't always petting.
 */
export function npcMoodToward(nodeId: string, isFriend: boolean, nowMs = Date.now()): NpcMood {
  if (isFriend) {
    return "friendly";
  }
  const temper = npcTemperament(nodeId);
  const window = Math.floor(nowMs / 120_000);
  const roll = hashNpcId(`${nodeId}:${window}`) % 100;
  if (temper === "warm") {
    return roll < 70 ? "friendly" : roll < 90 ? "busy" : "cold";
  }
  if (temper === "even") {
    return roll < 40 ? "friendly" : roll < 75 ? "busy" : "cold";
  }
  return roll < 18 ? "friendly" : roll < 55 ? "busy" : "cold";
}

/** After a friendly moment, will they offer pets? (friends more often). */
export function npcOffersPet(nodeId: string, isFriend: boolean, nowMs = Date.now()): boolean {
  const window = Math.floor(nowMs / 45_000);
  const roll = hashNpcId(`pet:${nodeId}:${window}`) % 100;
  if (isFriend) {
    return roll < 55;
  }
  return roll < 32;
}

const HI_LINES = [
  "Hi, pup!",
  "Oh, hello!",
  "Well hi there!",
  "Look at you!",
  "Hey, good dog!",
  "Aww, hi!",
];

const FRIEND_HI_LINES = [
  "There's my favorite!",
  "Hi again, buddy!",
  "You came back!",
  "Hey, friend!",
  "I know that face!",
  "Missed those paws!",
];

const PET_OFFER_LINES = [
  "Want pets?",
  "Come get scritches!",
  "Can I pet you?",
  "Who's a good dog?",
  "Ear rubs?",
  "Soft head? Yes please.",
];

const BUSY_LINES = [
  "Not right now, pup.",
  "Give me a minute…",
  "Busy paws - later!",
  "Maybe in a bit.",
  "I'm mid-bite. Hold on.",
];

const COLD_LINES = [
  "Shoo.",
  "Not today.",
  "Please don't.",
  "I'm not a petting zoo.",
  "Space, please.",
];

const PET_DONE_LINES = [
  "Best pup. Best day.",
  "Such soft ears!",
  "You're perfect.",
  "Good dog. Really good.",
  "Okay, that was cute.",
  "Come back anytime.",
];

function pick(lines: readonly string[], seed: string): string {
  return lines[hashNpcId(seed) % lines.length]!;
}

export function npcHiLine(nodeId: string, isFriend: boolean, nowMs = Date.now()): string {
  const pool = isFriend ? FRIEND_HI_LINES : HI_LINES;
  return pick(pool, `hi:${nodeId}:${Math.floor(nowMs / 30_000)}`);
}

export function npcPetOfferLine(nodeId: string, nowMs = Date.now()): string {
  return pick(PET_OFFER_LINES, `offer:${nodeId}:${Math.floor(nowMs / 45_000)}`);
}

export function npcBusyLine(nodeId: string, nowMs = Date.now()): string {
  return pick(BUSY_LINES, `busy:${nodeId}:${Math.floor(nowMs / 60_000)}`);
}

export function npcColdLine(nodeId: string, nowMs = Date.now()): string {
  return pick(COLD_LINES, `cold:${nodeId}:${Math.floor(nowMs / 90_000)}`);
}

export function npcPetDoneLine(nodeId: string, nowMs = Date.now()): string {
  return pick(PET_DONE_LINES, `done:${nodeId}:${nowMs}`);
}

export function isNpcFriend(friends: readonly string[] | undefined, nodeId: string): boolean {
  return !!friends?.includes(nodeId);
}

export function befriendNpc(friends: string[], nodeId: string): string[] {
  if (friends.includes(nodeId)) {
    return friends;
  }
  const next = [...friends, nodeId];
  if (next.length <= FRIEND_CAP) {
    return next;
  }
  return next.slice(next.length - FRIEND_CAP);
}

/** Base Begging XP for a pet (no item). */
export function petXpForLevel(beggingLevel: number): number {
  return 16 + Math.min(40, Math.floor(beggingLevel * 0.85));
}

export const PET_DURATION_MS = 1700;
export const PET_COOLDOWN_MS = 22_000;
